import { useMemo, type ReactNode } from 'react'
import { ReqdeskProvider as SdkReqdeskProvider } from '@reqdesk/sdk-react'
import type {
  ApiClient,
  StorageBackend,
  SubmitTicketData,
  TicketResult,
  TicketDetail,
  TicketListItem,
  TrackedTicketResult,
  CategoryItem,
  TagInfo,
  WidgetConfig as SdkWidgetConfig,
  ProjectListItem,
  AttachmentMeta,
  ApiKeyInfo,
  WidgetUser,
  AuthProvider,
  ThemeConfig,
  SupportedLanguage,
} from '@reqdesk/sdk-core'
import {
  submitTicket,
  trackTicket,
  submitTrackingReply,
  getCategories,
  createCategory,
  listProjectTags,
  createTag,
  getWidgetConfig,
  listProjects,
  closeTicket,
  uploadAttachment,
  uploadReplyAttachment,
  getAttachmentDownloadUrl,
  getTicketDetail,
  submitReply,
  resolveWidgetUser,
  listMyTickets,
} from '../api-client'
import { useReqdeskContext } from './ReqdeskProvider'

/**
 * Build an ApiClient-shaped object from the widget's standalone fetch
 * functions. The widget historically called these directly; sdk-react
 * views consume them via context. This adapter is the seam that lets
 * us collapse the widget's view duplicates onto sdk-react canonicals
 * without rewriting the underlying HTTP layer.
 */
function buildApiClientAdapter(): ApiClient {
  return {
    async submitTicket(projectId: string, data: SubmitTicketData): Promise<TicketResult> {
      return submitTicket(projectId, data) as Promise<TicketResult>
    },
    async getTicketDetail(ticketId: string, includeMedia = true): Promise<TicketDetail> {
      return getTicketDetail(ticketId, includeMedia) as Promise<TicketDetail>
    },
    async listMyTickets(projectId, userId, page = 1, pageSize = 20, showAll = false): Promise<TicketListItem[]> {
      return listMyTickets(projectId, userId, page, pageSize, showAll) as Promise<TicketListItem[]>
    },
    async closeTicket(ticketId: string): Promise<void> {
      await closeTicket(ticketId)
    },
    /** Legacy alias — the widget's HTTP layer uses the same close endpoint. */
    async resolveTicket(ticketId: string): Promise<void> {
      await closeTicket(ticketId)
    },
    async submitReply(ticketId: string, body: string): Promise<{ id: string }> {
      return submitReply(ticketId, body)
    },
    async submitTrackingReply(token: string, body: string): Promise<void> {
      await submitTrackingReply(token, body)
    },
    async trackTicket(token: string): Promise<TrackedTicketResult> {
      const result = await trackTicket(token)
      return result as TrackedTicketResult
    },
    async getCategories(projectId: string, parentId?: string | null): Promise<CategoryItem[]> {
      return getCategories(projectId, parentId) as Promise<CategoryItem[]>
    },
    async createCategory(projectId: string, name: string, parentId?: string | null): Promise<CategoryItem> {
      return createCategory(projectId, name, parentId) as Promise<CategoryItem>
    },
    async listProjectTags(projectId: string): Promise<TagInfo[]> {
      return listProjectTags(projectId) as Promise<TagInfo[]>
    },
    async createTag(projectId: string, name: string, color?: string): Promise<TagInfo> {
      return createTag(projectId, name, color) as Promise<TagInfo>
    },
    async getWidgetConfig(projectId: string): Promise<SdkWidgetConfig> {
      return getWidgetConfig(projectId) as Promise<SdkWidgetConfig>
    },
    async listProjects(workspaceId?: string): Promise<ProjectListItem[]> {
      return listProjects(workspaceId) as Promise<ProjectListItem[]>
    },
    async uploadAttachment(ticketId, file, onProgress): Promise<AttachmentMeta> {
      return uploadAttachment(ticketId, file, onProgress) as Promise<AttachmentMeta>
    },
    async uploadReplyAttachment(ticketId, replyId, file, onProgress): Promise<AttachmentMeta> {
      return uploadReplyAttachment(ticketId, replyId, file, onProgress) as Promise<AttachmentMeta>
    },
    async getAttachmentDownloadUrl(attachmentId: string) {
      return getAttachmentDownloadUrl(attachmentId)
    },
    async validateKey(): Promise<ApiKeyInfo> {
      // The widget doesn't expose validateKey today; sdk-react views never
      // call it. Stub it so the typecheck passes; if a future view starts
      // needing this, wire the real endpoint here.
      throw new Error('validateKey is not supported by the widget adapter')
    },
    async checkServerIdentity(_baseUrl: string) {
      throw new Error('checkServerIdentity is not supported by the widget adapter')
    },
    async resolveWidgetUser(projectId: string, email: string): Promise<WidgetUser | null> {
      return resolveWidgetUser(projectId, email) as Promise<WidgetUser | null>
    },
  }
}

/**
 * Storage adapter backed by `localStorage`, namespaced by api key.
 * Mirrors the widget's existing per-key storage isolation so the
 * sdk-react views don't see another tab's state.
 */
function buildStorageAdapter(apiKey: string): StorageBackend {
  const prefix = `reqdesk:${apiKey}:`
  return {
    async get<T>(key: string): Promise<T | null> {
      try {
        const raw = localStorage.getItem(prefix + key)
        if (raw == null) return null
        try { return JSON.parse(raw) as T } catch { return raw as unknown as T }
      } catch { return null }
    },
    async set<T>(key: string, value: T): Promise<void> {
      try {
        const payload = typeof value === 'string' ? value : JSON.stringify(value)
        localStorage.setItem(prefix + key, payload)
      } catch { /* quota / privacy mode — best-effort */ }
    },
    async remove(key: string): Promise<void> {
      try { localStorage.removeItem(prefix + key) } catch { /* ignore */ }
    },
  }
}

/**
 * Bridges the widget's existing ReqdeskContext (used by FloatingWidget
 * shell) to sdk-react's ReqdeskContext (used by the canonical views).
 * Render once at the top of the React tree, inside the widget provider
 * but above the FloatingWidget body, so children can read either
 * context interchangeably.
 */
export function SdkReactBridge({ children }: { children: ReactNode }) {
  const widgetCtx = useReqdeskContext()

  const apiClient = useMemo(() => buildApiClientAdapter(), [])
  const storage = useMemo(() => buildStorageAdapter(widgetCtx.apiKey), [widgetCtx.apiKey])

  // The widget's HTTP layer reads the OIDC bearer via setOidcTokenProvider
  // at boot — we don't need a separate AuthProvider for the request path.
  // sdk-react reads isAuthenticated via authProvider.isAuthenticated(); the
  // shim below mirrors the widget's auth state so the views' authenticated
  // branches (e.g. MyTicketsView skipping the email gate) fire correctly.
  const authProvider: AuthProvider | undefined = useMemo(() => {
    if (!widgetCtx.isAuthenticated) return undefined
    return {
      getAccessToken: async () => null,
      isAuthenticated: () => widgetCtx.isAuthenticated,
      getUserInfo: async () => widgetCtx.userEmail
        ? { id: widgetCtx.userEmail, email: widgetCtx.userEmail, name: widgetCtx.userName ?? null }
        : null,
    }
  }, [widgetCtx.isAuthenticated, widgetCtx.userEmail, widgetCtx.userName])

  const customer = useMemo(() => {
    if (widgetCtx.customer) return widgetCtx.customer
    if (widgetCtx.isAuthenticated && widgetCtx.userEmail) {
      return { email: widgetCtx.userEmail, name: widgetCtx.userName ?? undefined }
    }
    return undefined
  }, [widgetCtx.customer, widgetCtx.isAuthenticated, widgetCtx.userEmail, widgetCtx.userName])

  return (
    <SdkReqdeskProvider
      apiClient={apiClient}
      authProvider={authProvider}
      storage={storage}
      projectId={widgetCtx.projectId}
      language={widgetCtx.language as SupportedLanguage | undefined}
      translations={widgetCtx.translations}
      theme={widgetCtx.theme as ThemeConfig | undefined}
      customer={customer}
    >
      {children}
    </SdkReqdeskProvider>
  )
}
